/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/QaRunTextChecks.tsx
 * FILE_ID: JOBHUB-QA-RUN-TEXT-CHECKS-001
 * PURPOSE: Requirements and Criteria buttons for one Run step, and the tick panel.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useIsMdUp } from '@quotech/ui'
import { cn } from '@quotech/tenant-shared/lib/utils'

import { qaErrorMessage } from './qaTabSelectors'

export function qaCheckLines(text: string | null | undefined): string[] {
  if (!text) return []
  return text.split(/\r?\n/).map((line) => line.trim()).filter((line) => line.length > 0)
}

export type QaLineChecks = { requirement?: number[]; criteria?: number[] }
export type QaRunCheckKind = 'requirement' | 'criteria'

function toggled(current: number[] | undefined, index: number, checked: boolean): number[] {
  const next = new Set(current ?? [])
  if (checked) next.add(index)
  else next.delete(index)
  return [...next].sort((a, b) => a - b)
}

export function useQaRunTextChecks({
  itemId,
  requirementText,
  criteriaText,
  lineChecks,
  locked,
  onSave,
}: {
  itemId: string
  requirementText: string | null | undefined
  criteriaText: string | null | undefined
  lineChecks: QaLineChecks | null | undefined
  locked: boolean
  onSave: (kind: QaRunCheckKind, indexes: number[]) => Promise<void>
}): { buttons: ReactNode; panel: ReactNode } {
  const [open, setOpen] = useState<QaRunCheckKind | null>(null)
  const [pending, setPending] = useState<QaLineChecks | null>(null)
  const [error, setError] = useState<string | null>(null)
  const buttonRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const isMdUp = useIsMdUp()
  const shown = pending ?? lineChecks ?? {}

  useEffect(() => {
    setPending(null)
  }, [lineChecks])

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null
      if (buttonRef.current?.contains(target) || panelRef.current?.contains(target)) return
      setOpen(null)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(null)
    }
    document.addEventListener('pointerdown', onPointerDown, true)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const toggleKind = (kind: QaRunCheckKind) => {
    setError(null)
    setOpen((current) => (current === kind ? null : kind))
  }

  const toggleLine = async (kind: QaRunCheckKind, index: number, checked: boolean) => {
    if (locked) return
    const previous = pending
    const nextIndexes = toggled(shown[kind], index, checked)
    const next: QaLineChecks = { ...shown, [kind]: nextIndexes }
    setPending(next)
    setError(null)
    try {
      await onSave(kind, nextIndexes)
    } catch (caught) {
      setPending(previous)
      setError(qaErrorMessage(caught, 'Could not save that tick.'))
    }
  }

  const buttonClass = cn(
    'inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-border px-2.5 py-1.5 text-xs text-muted-foreground hover:bg-accent hover:text-foreground',
    !isMdUp && 'min-h-[44px]',
  )
  const panelId = `qa-run-check-panel-${itemId}`

  const kindButton = (kind: QaRunCheckKind, label: string, testId: string, control: string) => (
    <button
      type="button"
      className={cn(buttonClass, open === kind && 'bg-accent text-foreground')}
      aria-expanded={open === kind}
      aria-controls={panelId}
      data-testid={testId}
      data-qa-phone-control={control}
      onClick={() => toggleKind(kind)}
    >
      {label}
    </button>
  )

  const buttons = (
    <div ref={buttonRef} className="contents">
      {kindButton('requirement', 'Requirements', `qa-run-requirements-${itemId}`, 'qa-phone-requirements')}
      {kindButton('criteria', 'Criteria', `qa-run-criteria-${itemId}`, 'qa-phone-criteria')}
    </div>
  )

  const lines = open === 'requirement' ? qaCheckLines(requirementText) : open === 'criteria' ? qaCheckLines(criteriaText) : []
  const empty = open === 'requirement' ? 'No requirement on this step.' : 'No criteria on this step.'
  const indexes = open ? shown[open] ?? [] : []

  const panel = open ? (
    <div
      ref={panelRef}
      id={panelId}
      role="region"
      aria-label={open === 'requirement' ? 'Requirements' : 'Criteria'}
      className="mt-2 max-h-[45vh] overflow-y-auto rounded-lg border border-border bg-card p-2"
    >
      {lines.length === 0 ? (
        <p className="text-sm text-muted-foreground">{empty}</p>
      ) : (
        lines.map((line, index) => (
          <label
            key={`${open}-${index}`}
            className="flex min-h-[44px] items-start gap-2 py-1 text-sm text-foreground md:min-h-0"
          >
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-border"
              checked={indexes.includes(index)}
              disabled={locked}
              aria-label={`${open === 'requirement' ? 'Requirement' : 'Criterion'} ${index + 1}`}
              onChange={(event) => { void toggleLine(open, index, event.target.checked) }}
            />
            <span className="min-w-0 flex-1 whitespace-pre-wrap break-words">{line}</span>
          </label>
        ))
      )}
      {error ? <p role="alert" className="text-xs text-destructive">{error}</p> : null}
    </div>
  ) : null

  return { buttons, panel }
}
